'use client';

import Image from 'next/image';
import { Banner } from "@/components/shared/banner";
import { TableOfContents } from '@/features/marketing/components/blog/TableOfContents';

export default function TrustworthyAndResponsibleAIPage() {
  const contentSections = [
    { id: 'purpose', heading: 'Purpose and Scope' },
    { id: 'features', heading: 'AI-powered study features' },
    { id: 'principles', heading: 'Core Principles' },
    { id: 'practices', heading: 'Key Practices' },
    { id: 'compliance', heading: 'Compliance & Governance' },
    { id: 'review', heading: 'Review and Continuous Improvement' },
    { id: 'contact', heading: 'Contact and Feedback' },
  ];

  return (
    <main className="min-h-screen bg-white" role="main" aria-label="Trustworthy and Responsible AI Statement">
      <Banner variant="with-svg" className="mb-10">
        <div className="text-white ml-8 lg:ml-32">
          <p className="text-sm mb-4 font-bold text-white">
            Last Updated: 21st July 2025
          </p>
          <h1 className="text-4xl lg:text-5xl font-bold mb-2 leading-tight text-white">Trustworthy and Responsible AI</h1>
          <p className="text-lg lg:text-xl mb-4 leading-relaxed opacity-90 text-white">
            Our commitment to ethical and responsible use of AI technology
          </p>
        </div>
      </Banner>

      <div className="flex max-w-7xl mx-auto pb-8 sm:pb-16">
        {/* Sticky Sidebar */}
        <aside className="hidden lg:block w-96 shrink-0" role="complementary" aria-label="Table of contents">
          <div className="sticky top-0 pt-7 px-4 pb-16">
            <TableOfContents subject={{ contentSections }} />
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 lg:ml-8">
          <div className="max-w-4xl mx-auto lg:mx-0 px-3 sm:px-4 pt-2 sm:pt-4 lg:pt-8 pb-6 sm:pb-8 lg:px-8">

            <article className="prose prose-sm sm:prose-base lg:prose-lg max-w-none mt-4 sm:mt-6 lg:mt-8" role="article" aria-label="Trustworthy and Responsible AI content">
              <h2 id="statement" className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8">
              Our Commitment to Building Trustworthy and Responsible AI
              </h2>

              <p className="text-gray-700 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base">
              We’re committed to building AI that supports students responsibly. From clear labelling and human oversight to fairness, safety, privacy, and accountability, our principles ensure AI remains a trustworthy study partner—not a replacement for you.
              </p>

              <Image 
                src="/uob-aston-webb.jpg" 
                alt="University of Birmingham, Aston Webb" 
                width={1200}
                height={675}
                className="w-full h-auto rounded-lg shadow-md"
              />
              
              <p className="text-gray-700 pt-4 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base">
                This statement applies to the website <a href="https://studystash.com" className="text-blue-600 hover:text-blue-800 underline">StudyStash.com</a> and all AI-powered features within our platform.
              </p>
              
              <h2 id="purpose" className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mt-12 sm:mt-16 lg:mt-20 mb-2 sm:mb-3 lg:mb-4">
                Purpose and Scope
              </h2>
              
              <p className="text-gray-700 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base">
                StudyStash helps students get answers from their course materials and creates study aids. This document explains how we use AI safely and what you need to know about using our tools. AI can make mistakes - always check important information before using it in your studies.
              </p>
              
              <div className="mb-4 sm:mb-6 lg:mb-8 pt-4">
                <Image 
                  src="/our_values.png" 
                  alt="StudyStash Our Values" 
                  width={1200}
                  height={675}
                  className="w-full h-auto rounded-lg shadow-md"
                />
              </div>



              <h2 id="principles" className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mt-12 sm:mt-16 lg:mt-20 mb-2 sm:mb-3 lg:mb-4">
                Core Principles
              </h2>
              
              <p className="text-gray-700 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base">
                We follow these key principles to keep our AI safe and useful:
              </p>

              <div className="mb-4 sm:mb-6 lg:mb-8 pt-4">
                <Image 
                  src="/responsible_ai_practice.png" 
                  alt="StudyStash Responsible AI Framework" 
                  width={1200}
                  height={675}
                  className="w-full h-auto rounded-lg shadow-md"
                />
              </div>
              
              <h2 id="practices" className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mt-12 sm:mt-16 lg:mt-20 mb-2 sm:mb-3 lg:mb-4">
                Trustworthy AI principles in practice
              </h2>
              
              <p className="text-gray-700 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base">
              Here's how we put these principles into practice:
              </p>
              
              <h3 className="text-sm sm:text-base lg:text-lg xl:text-xl font-bold text-gray-900 mt-3 sm:mt-4 lg:mt-6 mb-2 sm:mb-3 lg:mb-4">
                Transparency and Explainability
              </h3>
              
              <ul className="list-disc pl-3 sm:pl-4 lg:pl-6 mb-3 sm:mb-4 lg:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem">AI features are clearly marked with sparkle (✨) icons</li>
                <li role="listitem">AI-generated content is labeled so you know it came from AI</li>
                <li role="listitem">'Why did this appear?' buttons explain how AI found answers in your course materials</li>
                <li role="listitem">Always check AI answers against your course materials</li>
              </ul>
              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Reliability and Accuracy
              </h3>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                We prioritise reliability and accuracy by making it clear that AI can produce incorrect or misleading content, and that all outputs should be carefully reviewed.
              </p>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem">AI-generated content may occasionally be inaccurate or incomplete. Users are expected to verify outputs against trusted course materials.</li>
                <li role="listitem">We make it clear that AI should only be used to support study and revision based on course content, not for tasks beyond its intended use.</li>
                <li role="listitem">All AI-generated content is editable, giving users full control to adjust, revise or remove outputs before using them.</li>
                <li role="listitem">We recognise that AI can reflect bias and focus on use cases that reduce this risk.</li>
                <li role="listitem">We intentionally limit AI use to educational content, avoiding areas where bias is more likely or more harmful.</li>
                <li role="listitem">We have a document specifically on these two points which you can request at <a href="mailto:support@studystash.com" className="text-blue-600 hover:text-blue-800 underline">support@studystash.com</a></li>
              </ul>
              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Fairness
              </h3>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                We acknowledge that AI systems can reflect or amplify societal biases. To reduce this risk StudyStash intentionally limits AI use to educational content and study support while avoiding high-risk or sensitive use cases. While we design features to minimise bias we encourage users to review outputs carefully and use their judgment when incorporating AI-generated content into their learning.
              </p>
              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Privacy and Security
              </h3>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem">We protect your privacy and keep your data secure</li>
                <li role="listitem">Don't include personal information when asking AI questions</li>
                <li role="listitem">Your course data is never used to improve AI models without permission</li>
                <li role="listitem">We follow GDPR and other privacy laws</li>
                <li role="listitem">Find more details in our <a href="https://trust.studystash.com" className="text-blue-600 hover:text-blue-800 underline">Trust Centre</a></li>
              </ul>
              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Safety
              </h3>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                We acknowledge that AI systems may occasionally produce inappropriate or unsafe content. We clearly flag within the platform that AI-generated output should be reviewed and verified before use. Students are advised to check outputs for accuracy and appropriateness and to consult their lecturers or educators if they are unsure.
              </p>
              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Humans in Control
              </h3>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                AI features are designed to support learning without removing human oversight. Users remain in control of when and how AI is used, and all outputs can be reviewed and edited before use. StudyStash's AI systems do not make automated decisions that have legal academic or other significant effects on individuals.
              </p>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem">AI features are optional and can be turned on or off as needed - for example when creating flashcards or summaries.</li>
                <li role="listitem">AI-generated content is always editable and can be shared for peer feedback and review.</li>
              </ul>
              

              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Accessibility
              </h3>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                We are WCAG 2.2 AA compliant. View our <a href="https://studystash.com/accessibility-statement" className="text-blue-600 hover:text-blue-800 underline">accessibility statement</a> for more information.
              </p>
              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Accountability
              </h3>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem">We work with certified AI providers to keep your data safe</li>
                <li role="listitem">We follow GDPR and other privacy laws</li>
                <li role="listitem">We regularly test our security to protect your information</li>
                <li role="listitem">We monitor AI accuracy through user feedback and regular testing</li>
                <li role="listitem">You can give us feedback on AI answers using thumbs-up/down ratings</li>
              </ul>
              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Performance Monitoring
              </h3>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                We monitor AI accuracy through user feedback and regular testing to assess:
              </p>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem"><strong>Factuality:</strong> outputs are factually consistent with trusted references</li>
                <li role="listitem"><strong>Relevance:</strong> responses align with LMS content and address user queries effectively</li>
                <li role="listitem"><strong>Faithfulness:</strong> verification that outputs are directly supported by input context</li>
                <li role="listitem"><strong>Quality:</strong> rubric-based grading and semantic similarity assessments</li>
                <li role="listitem"><strong>Operational Efficiency:</strong> API latency and cost tracking</li>
              </ul>
              
              <h2 id="compliance" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Compliance & Governance
              </h2>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                We follow GDPR and work with certified AI providers to keep your data safe and secure.
              </p>
              
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Internal Responsibilities
              </h3>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                Our CTO, <a href="https://studystash.com/team" className="text-blue-600 hover:text-blue-800 underline">Jonathan Graham</a>, oversees AI safety and ensures all AI systems align with our principles and commitments.
              </p>
              
              <h2 id="review" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Review and Continuous Improvement
              </h2>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                StudyStash reviews AI usage regularly due to rapid technology changes.
              </p>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem"><strong>Quarterly Reviews:</strong> AI systems and practices assessed against our principles and commitments</li>
                <li role="listitem"><strong>Multi-stakeholder Input:</strong> Reviews include AI safety team, external advisors and user feedback</li>
                <li role="listitem"><strong>Proactive Updates:</strong> Practices updated in response to new risks, findings or standards</li>
                <li role="listitem"><strong>Industry Alignment:</strong> Staying current with AI safety research, regulatory developments and best practices</li>
              </ul>
              
              <h2 id="features" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                AI-powered study features
              </h2>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                StudyStash uses LLM capabilities for these features. We do not currently use any in-house AI models and partner with certified vendors who hold <a href="https://www.iso.org/standard/27001" className="text-blue-600 hover:text-blue-800 underline">ISO 27001</a> or SOC 2 Type II certification:
              </p>
              
              <div className="mb-6 sm:mb-8 pt-4">
                <Image 
                  src="/ai_feature_process.png" 
                  alt="StudyStash AI Features" 
                  width={1200}
                  height={675}
                  className="w-full h-auto rounded-lg shadow-md"
                />
              </div>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem"><strong>StudyChat:</strong> AI-powered document querying for instant answers from uploaded study materials</li>
                <li role="listitem"><strong>Practice Tests:</strong> Automated creation and marking of practice questions and assessments</li>
                <li role="listitem"><strong>Supplementary Notes:</strong> AI-generated additional study materials and explanations</li>
                <li role="listitem"><strong>Flashcards from Notes:</strong> Intelligent creation of flashcards from uploaded study materials</li>
                <li role="listitem"><strong>Insights for Lecturer Reports:</strong> AI-generated insights and analytics for educators</li>
                <li role="listitem"><strong>Document OCR & Processing:</strong> Intelligent text extraction and processing from uploaded documents</li>
              </ul>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                All features follow our Trustworthy AI principles with transparency and user control. AI features are identified with sparkle (✨) icons and warning flags. Institutional data is never used to train third-party or external models without explicit signed consent from institutions.
              </p>
              

              
              <h2 id="contact" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Contact and Feedback
              </h2>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                If you have concerns about our AI systems, need information about our AI practices, want to report potential issues, or need access to our fair use policy:
              </p>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem">Email <a href="mailto:ai-safety@studystash.com" className="text-blue-600 hover:text-blue-800 underline">ai-safety@studystash.com</a></li>
                <li role="listitem">Email <a href="mailto:support@studystash.com" className="text-blue-600 hover:text-blue-800 underline">support@studystash.com</a></li>
                <li role="listitem">Visit our <a href="https://studystash.com/contact-us" className="text-blue-600 hover:text-blue-800 underline">contact us</a> page</li>
                <li role="listitem">Fair use policy ensures responsible usage without restrictive limits on AI feature access. Request policy in our <a href="https://trust.studystash.com" className="text-blue-600 hover:text-blue-800 underline">Trust Centre</a></li>
              </ul>
              
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Further information
              </h2>
              
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                For additional information about our AI practices and commitments, please refer to:
              </p>
              
              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1" role="list">
                <li role="listitem"><a href="https://studystash.com/trustworthy-and-responsible-ai" className="text-blue-600 hover:text-blue-800 underline">StudyStash's Trustworthy and Responsible AI Framework</a></li>
                <li role="listitem"><a href="https://studystash.com/privacy-policy" className="text-blue-600 hover:text-blue-800 underline">StudyStash's Privacy Policy</a></li>
                <li role="listitem"><a href="https://studystash.com/terms-of-use" className="text-blue-600 hover:text-blue-800 underline">StudyStash's Terms of Use</a></li>
                <li role="listitem"><a href="https://studystash.com/accessibility-statement" className="text-blue-600 hover:text-blue-800 underline">StudyStash's Accessibility Statement</a></li>
              </ul>
              
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Abbreviations
              </h2>
              
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <table className="min-w-full border border-gray-300 text-xs sm:text-sm lg:text-base">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-left font-semibold text-gray-900">Abbreviation</th>
                      <th className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-left font-semibold text-gray-900">Full Term</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-700">AI</td>
                      <td className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-700">Artificial Intelligence</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-700">GDPR</td>
                      <td className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-700">General Data Protection Regulation</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-700">ISO27001</td>
                      <td className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-700">International Organisation for Standardisation 27001 (Information Security Management)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-700">RTAI</td>
                      <td className="border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-700">Responsible and Trustworthy Artificial Intelligence</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </article>
          </div>
        </div>
      </div>
    </main>
      );
  } 